// components/entities/tables/leads/LeadsTable.tsx
'use client';

import React, { useMemo, useState, useCallback } from 'react';
import { isScored } from '#lib/leasing/leadStatus';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { useLeadDependencyHydration } from '@/components/entities/useLeadDependencyHydration';
import { Download, ThumbsUp, ThumbsDown, Activity, Clock, FileText, Gauge, Landmark, MessageCircle, MessageSquare, Send, Sparkles, Store } from '@/components/ui/lucide-memo';
import { EntityBulkAction, EntityRowAction, LeasingLeadDTO, LeasingLeadFilters, PortfolioFitDTO, SourcingCriterionResult } from '#types/types';
import { Table, TableColumnDef, type TableContextMenuAction, type TableFilterOption, type TableRowSelectionContext } from '../../../portal/Table';
import { toast } from 'sonner';
import { buildCsvContent as buildCsvRowsContent, downloadCsv } from '@/lib/portal/csvExport';
import { ViewModeGraphicToggle, type InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { supportsInventoryViewModeToggle } from '@/components/portal/inventoryViewModeCapabilities';
import { prospectionColumnHeaders } from '@/components/entities/prospectionCells';
import { CriteriaLegend, CriterionHeader } from '@/components/entities/shortlistCells';
import { criterionTitle } from '#lib/leasing/criterionMatch';
import { ChevronLeft } from '@/components/ui/lucide-memo';

/** How many criteria a shortlist shows before its expand handle: the heaviest ones. */
const CRITERIA_SHOWN = 3;

/** The open campaign's criteria, read off one of its scored rows: the criteria column's legend and width. */
const useCampaignCriteria = (campaignId: string | null) => {
  const registry = useKernelStore((state) => state.inventory.Lead.registry);
  return useMemo(() => {
    if (!campaignId) return null;
    for (const lead of Object.values(registry) as Array<LeasingLeadDTO | undefined>) {
      if (lead?.lastCampaignId === campaignId && lead.criteriaResults && lead.criteriaResults.length > 0) return lead.criteriaResults;
    }
    return null;
  }, [registry, campaignId]);
};

const translations = {
  en: {
    sortFitScore: 'Fit Score',
    sortStatus: 'Status',
    sortUpdatedAt: 'Last Updated',
    sortCreatedAt: 'Created Date',
    property: 'Property',
    brand: 'Brand',
    leadSignal: 'Criteria',
    leadFit: 'Fit',
    portfolioSignal: 'Portfolio signal',
    portfolioFit: 'Portfolio fit',
    campaign: 'Campaign',
    status: 'Status',
    actions: 'Actions',
    markInteresting: 'Mark interesting',
    markDismissed: 'Mark dismissed',
    exportCsv: 'Export CSV',
    exportCsvSuccess: (count: number) => `${count} lead(s) exported.`,
    exportCsvFailed: 'Failed to export selected leads.',
  },
  fr: {
    sortFitScore: 'Score de fit',
    sortStatus: 'Statut',
    sortUpdatedAt: 'Derniere maj',
    sortCreatedAt: 'Date creation',
    property: 'Actif',
    brand: 'Marque',
    leadSignal: 'Critères',
    leadFit: 'Fit',
    portfolioSignal: 'Signal portfolio',
    portfolioFit: 'Fit portfolio',
    campaign: 'Campagne',
    status: 'Statut',
    actions: 'Actions',
    markInteresting: 'Marquer interessant',
    markDismissed: 'Marquer ecarte',
    exportCsv: 'Exporter CSV',
    exportCsvSuccess: (count: number) => `${count} lead(s) exporte(s).`,
    exportCsvFailed: "Echec de l'export des leads selectionnes.",
  },
};

const EXPANDED_PANEL_STYLE: React.CSSProperties = { width: 'var(--table-viewport-width, 100%)' };

interface LeadsTableProps {
  initialFilters?: Partial<LeasingLeadFilters>;
  lockedFilters?: Partial<LeasingLeadFilters>;
  /** Client-side row gate on top of the server filters (see Table.clientRowFilter). */
  clientRowFilter?: (lead: { status?: string | null }) => boolean;
  /** Custom empty-state body (see Table.emptyStateContent). */
  emptyStateContent?: React.ReactNode;
  /** Rows that are still coming, queued after the real ones (see Table.afterRowsContent). */
  afterRowsContent?: React.ReactNode | ((columns?: TableColumnDef[]) => React.ReactNode);
  /** Re-rank the loaded rows client-side as scores change (see Table.liveResort). */
  liveResort?: boolean;
  /** True while a re-score is reworking these rows (see Table.rescoreActive). */
  rescoreActive?: boolean;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  renderCustomToolbarHeader?: () => React.ReactNode;
  /** Leads the toolbar's left side, before the tools (the shortlist's owner). */
  toolbarLead?: React.ReactNode;
  toolbarContextMenuActions?: TableContextMenuAction[];
  leadLockedUnitId?: string;
  propertyLocked?: boolean;
  scopedIds?: string[] | null;
  disableManualRefresh?: boolean;
  onManualRefresh?: () => void | Promise<void>;
  bulkActions?: EntityBulkAction<LeasingLeadDTO>[];
  includeDefaultBulkActions?: boolean;
  liveRefresh?: boolean;
  enableSelection?: boolean;
  controlledSelectionIds?: string[];
  onControlledSelectionChange?: (ids: string[]) => void;
  viewModeVisual?: InventoryVisualMode;
  onViewModeVisualChange?: (mode: InventoryVisualMode) => void;
  glass?: boolean;
  inlineExpand?: boolean;
  onExpandedIdChange?: (id: string | null) => void;
  /** Controls the expanded row; forwarded to Table so the store can own it. */
  expandedId?: string | null;
}

export const LeadsTable = React.memo(({
  initialFilters,
  lockedFilters,
  clientRowFilter,
  emptyStateContent,
  afterRowsContent,
  liveResort,
  rescoreActive,
  assetLedgerTitle = 'All leads',
  assetLedgerBreadcrumb = 'Inventory / Leads',
  renderCustomToolbarHeader,
  toolbarLead,
  toolbarContextMenuActions,
  leadLockedUnitId,
  propertyLocked = false,
  scopedIds,
  disableManualRefresh,
  onManualRefresh,
  bulkActions,
  includeDefaultBulkActions = true,
  liveRefresh,
  enableSelection = false,
  controlledSelectionIds,
  onControlledSelectionChange,
  viewModeVisual,
  onViewModeVisualChange,
  glass,
  inlineExpand,
  onExpandedIdChange,
  expandedId,
}: LeadsTableProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const userContext = useKernelStore((state) => state.userContext);
  const t = translations[lang];
  const [localViewModeVisual, setLocalViewModeVisual] = useState<InventoryVisualMode>('table');
  const resolvedViewModeVisual = viewModeVisual ?? localViewModeVisual;
  const handleViewModeVisualChange = useCallback((mode: InventoryVisualMode) => {
    onViewModeVisualChange?.(mode);
    if (!onViewModeVisualChange) {
      setLocalViewModeVisual(mode);
    }
  }, [onViewModeVisualChange]);

  // Hydratation des Brands et Properties pour les leads visibles
  const handleVisibleIdsChange = useLeadDependencyHydration();

  const sortOptions = useMemo(
    () => [
      // A row waiting to be (re)scored sorts as unscored, whatever score an earlier run left on it:
      // pending rows kept stale scores and sat above the brands this run had just scored (2026-09-28).
      { label: t.sortFitScore, field: 'fitScore', localAccessor: (lead: any) => (isScored(lead?.status) ? lead?.fitScore ?? null : null) },
      { label: t.sortStatus, field: 'status' },
      { label: t.sortUpdatedAt, field: 'updatedAt' },
      { label: t.sortCreatedAt, field: 'createdAt' },
    ],
    [t.sortFitScore, t.sortStatus, t.sortUpdatedAt, t.sortCreatedAt]
  );
  
  const filterOptions = useMemo<TableFilterOption<'Lead'>[]>(
    () => [
      { key: 'brandName', label: t.brand },
      { key: 'status', label: t.status },
      { key: 'fitScore', label: t.leadFit },
      { key: 'lastCampaignId', label: t.campaign },
    ],
    [t.brand, t.leadFit, t.campaign, t.status]
  );

  const hidePropertyColumn = Boolean(propertyLocked || lockedFilters?.propertyId || initialFilters?.propertyId);

  const campaignCriteria = useCampaignCriteria(lockedFilters?.lastCampaignId ?? null);
  const [criteriaExpanded, setCriteriaExpanded] = useState(false);
  const columns = useMemo<TableColumnDef[]>(() => {
    // With a campaign rubric, one column per criterion (C1..Cn), the heaviest CRITERIA_SHOWN on.
    const criteriaColumns: (TableColumnDef & { isHidden?: boolean })[] | null = campaignCriteria?.length
      ? (() => {
          const shown = new Set(
            campaignCriteria
              .map((criterion, index) => ({ criterion, index }))
              .sort((a, b) => b.criterion.maxScore - a.criterion.maxScore || a.index - b.index)
              .slice(0, CRITERIA_SHOWN)
              .map(({ criterion }) => criterion.criterionId),
          );
          const folded = campaignCriteria.length - shown.size;
          const columnsForCriteria: (TableColumnDef & { isHidden?: boolean })[] = campaignCriteria.map((criterion, index) => ({
            id: `criterion:${criterion.criterionId}`,
            icon: Sparkles,
            minWidth: 84,
            header: <CriterionHeader code={`C${index + 1}`} title={criterionTitle(criterion.label)} />,
            label: `C${index + 1} · ${criterionTitle(criterion.label)}`,
            defaultHidden: !criteriaExpanded && !shown.has(criterion.criterionId),
          }));
          if (folded > 0) {
            columnsForCriteria.push({
              // The expand handle: '+N' unfolds every criterion, the chevron folds them back.
              id: '_criteriaHandle',
              icon: Sparkles,
              minWidth: 64,
              header: (
                <button
                  type="button"
                  onClick={() => setCriteriaExpanded((open) => !open)}
                  aria-label={criteriaExpanded ? (lang === 'fr' ? 'Replier les critères' : 'Fold the criteria') : (lang === 'fr' ? 'Afficher tous les critères' : 'Show every criterion')}
                  // Plain header text like C1..Cn beside it, lit on hover: a filled chip here stood
                  // out of the header row (owner, 2026-09-28).
                  className="inline-flex items-center gap-0.5 text-inherit transition-colors hover:text-white"
                  style={{ fontSize: 'inherit' }}
                >
                  {criteriaExpanded ? <ChevronLeft size={14} strokeWidth={2.2} /> : `+${folded}`}
                </button>
              ),
            });
          }
          return columnsForCriteria;
        })()
      : null;
    // Unified with PortfolioFitsTable: a single rubric `signal` + a single `fit` score
    // (the LeadRenderer's generic signal/fit cells, identical to PortfolioFitRenderer).
    // The criteria breakdown lives in the detail's Scoring tab, not crammed into the list.
    const ph = prospectionColumnHeaders(lang);
    const allColumns: (TableColumnDef & { isHidden?: boolean })[] = [
      { id: 'property', icon: Landmark, minWidth: 180, header: t.property, isHidden: hidePropertyColumn },
      { id: 'brand', icon: Store, minWidth: 200, header: t.brand },
      // Wide enough for EVERY criterion's bar and name (60px + 8px gap each, plus cell padding):
      // an 11-criterion rubric clipped to five bars hid what the rest measured.
      // The verdict first, then what it is made of (owner, 2026-09-28).
      { id: 'fit', icon: Gauge, minWidth: 80, header: t.leadFit },
      ...(criteriaColumns ?? [{ id: 'signal', icon: Sparkles, minWidth: 360, header: <CriteriaLegend criteria={null} fallback={t.leadSignal} /> }]),
      { id: 'status', icon: Activity, minWidth: 100, header: t.status },
      { id: 'evaluated', icon: Clock, minWidth: 120, header: lang === 'fr' ? 'Noté' : 'Scored' },
      { id: 'prospectionContacted', icon: Send, minWidth: 90, header: ph.contacted },
      { id: 'prospectionChannel', icon: MessageSquare, minWidth: 110, header: ph.channel },
      { id: 'prospectionResponse', icon: MessageCircle, minWidth: 110, header: ph.response },
      { id: 'prospectionComment', icon: FileText, minWidth: 220, header: ph.comment },
    ];
    return allColumns.filter((column) => !column.isHidden);
  }, [hidePropertyColumn, t, lang, campaignCriteria, criteriaExpanded]);

  const canModerateLead = useCallback((lead: LeasingLeadDTO) => {
    const property = kernel.current?.getInventory().Property.registry[lead.propertyId];
    if (!property) return false;

    const isFaterAdmin = Boolean(userContext.isFaterAdmin);
    const isAdminOfPortfolioOrganisation = Boolean(
      userContext.isOrganisationAdmin &&
      userContext.organisationId &&
      property.portfolioOrganisationId &&
      userContext.organisationId === property.portfolioOrganisationId
    );
    const isAssignedLeasingManager = Boolean(
      userContext.dbUserId &&
      Array.isArray(property.leasingManagerIds) &&
      property.leasingManagerIds.includes(userContext.dbUserId)
    );

    return isFaterAdmin || isAdminOfPortfolioOrganisation || isAssignedLeasingManager;
  }, [
    kernel,
    userContext.dbUserId,
    userContext.isFaterAdmin,
    userContext.isOrganisationAdmin,
    userContext.organisationId,
  ]);

  const rowActions = useMemo<EntityRowAction<LeasingLeadDTO>[]>(() => [
    {
      id: 'flag-lead-interesting',
      label: t.markInteresting,
      icon: <ThumbsUp size={13} strokeWidth={2} className="text-emerald-300/90" />,
      visible: canModerateLead,
      onClick: async (lead) => {
        if (!kernel.current) return;
        await kernel.current.flagLeadInteresting(lead.id);
      },
    },
    {
      id: 'flag-lead-dismissed',
      label: t.markDismissed,
      icon: <ThumbsDown size={13} strokeWidth={2} className="text-rose-300/90" />,
      visible: canModerateLead,
      onClick: async (lead) => {
        if (!kernel.current) return;
        await kernel.current.flagLeadDismissed(lead.id);
      },
    },
  ], [
    canModerateLead,
    kernel,
    t.markInteresting,
    t.markDismissed,
  ]);

  const buildLeadCsvContent = useCallback((
    leads: LeasingLeadDTO[],
    lookup: {
      propertyRegistry: Record<string, { name?: string | null } | undefined>;
      brandRegistry: Record<string, { name?: string | null; category?: string | null; websiteUrl?: string | null } | undefined>;
      portfolioFitRegistry: Record<string, PortfolioFitDTO | undefined>;
    }
  ): string => {
    const headers = [
      'propertyName',
      'brandName',
      'brandCategory',
      'brandWebsiteUrl',
      'status',
      'fitScore',
      'portfolioFitScore',
      'portfolioFitRubrics',
      'nearestStoreName',
      'nearestStoreDistance',
      'rationale',
    ];

    const rows = leads.map((lead) => {
      const property = lookup.propertyRegistry[lead.propertyId];
      const brand = lookup.brandRegistry[lead.brandId];
      const portfolioFit = lead.portfolioFitId ? lookup.portfolioFitRegistry[lead.portfolioFitId] : undefined;
      const portfolioFitRubrics = Array.isArray(portfolioFit?.criteriaResults)
        ? portfolioFit.criteriaResults
            .map((criterion: SourcingCriterionResult) => `${criterion.label}:${criterion.score}/${criterion.maxScore}`)
            .join(' | ')
        : '';
      const values = [
        property?.name ?? '',
        brand?.name ?? '',
        brand?.category ?? '',
        brand?.websiteUrl ?? '',
        lead.status ?? '',
        lead.fitScore,
        typeof portfolioFit?.score === 'number' && Number.isFinite(portfolioFit.score) ? portfolioFit.score : '',
        portfolioFitRubrics,
        lead.nearestStoreName ?? '',
        lead.nearestStoreDistance ?? '',
        lead.rationale ?? '',
      ];
      return values;
    });

    return buildCsvRowsContent(headers, rows);
  }, []);

  const downloadLeadCsv = useCallback((leads: LeasingLeadDTO[]) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel unavailable');
    if (!leads.length) return;
    const complete = k.beginSourceAction?.({ action: 'export', origin: 'human', data: { entityType: 'Lead', count: leads.length, format: 'csv' } });
    try {
    const inventory = k.getInventory();
    const csv = buildLeadCsvContent(leads, {
      propertyRegistry: inventory.Property.registry as Record<string, { name?: string | null } | undefined>,
      brandRegistry: inventory.Brand.registry as Record<string, { name?: string | null; category?: string | null; websiteUrl?: string | null } | undefined>,
      portfolioFitRegistry: inventory.PortfolioFit.registry as Record<string, PortfolioFitDTO | undefined>,
    });
    downloadCsv('fater_leads_export', csv);
    complete?.('download_started');
    } catch (error) {
      complete?.('client_failed');
      throw error;
    }
  }, [buildLeadCsvContent, kernel]);

  const exportBulkAction = useMemo<EntityBulkAction<LeasingLeadDTO>>(() => ({
    id: 'export-selected-leads-csv',
    label: t.exportCsv,
    icon: <Download size={13} />,
    disabled: (selectedLeads) => selectedLeads.length === 0,
    onClick: async (selectedLeads) => {
      try {
        if (!selectedLeads.length) return;
        downloadLeadCsv(selectedLeads);
        toast.success(t.exportCsvSuccess(selectedLeads.length));
      } catch (error) {
        console.error('[LeadsTable] CSV export failed', error);
        toast.error(t.exportCsvFailed);
      }
    },
  }), [downloadLeadCsv, t]);

  const effectiveBulkActions = useMemo<EntityBulkAction<LeasingLeadDTO>[]>(() => {
    const provided = Array.isArray(bulkActions) ? bulkActions : [];
    if (!includeDefaultBulkActions) {
      return provided;
    }
    const defaults = [exportBulkAction];
    const merged = [...defaults.filter((action) => !provided.some((p) => p.id === action.id)), ...provided];
    return merged;
  }, [bulkActions, exportBulkAction, includeDefaultBulkActions]);

  const canShowViewModeToggle =
    supportsInventoryViewModeToggle('Lead') &&
    typeof onViewModeVisualChange === 'function';
  const composedToolbarHeader = useCallback(() => {
    if (!canShowViewModeToggle && !renderCustomToolbarHeader) return null;
    return (
      <>
        {canShowViewModeToggle && (
          <ViewModeGraphicToggle
            mode={resolvedViewModeVisual}
            onModeChange={handleViewModeVisualChange}
            className={renderCustomToolbarHeader ? 'mr-1' : undefined}
          />
        )}
        {renderCustomToolbarHeader?.()}
      </>
    );
  }, [
    canShowViewModeToggle,
    handleViewModeVisualChange,
    renderCustomToolbarHeader,
    resolvedViewModeVisual,
  ]);
  const effectiveToolbarHeader =
    canShowViewModeToggle || renderCustomToolbarHeader ? composedToolbarHeader : undefined;

  const renderLeadRow = useCallback((
    lead: LeasingLeadDTO,
    isExpanded: boolean,
    toggle: () => void,
    currentColumns?: TableColumnDef[],
    selectionContext?: TableRowSelectionContext,
  ) => (
    <div className="flex w-full min-w-[var(--table-min-width)] flex-none flex-col">
      <EntityView
        key={lead.id}
        type="Lead"
        entityId={lead.id}
        variant="row"
        onClick={toggle}
        columns={currentColumns}
        rowActions={rowActions}
        isSelectable={selectionContext?.isSelectable}
        isSelected={selectionContext?.isSelected}
        onToggleSelect={selectionContext?.onToggleSelect}
        onSelectionZoneMouseDown={selectionContext?.onSelectionZoneMouseDown}
        leadLockedUnitId={leadLockedUnitId}
        className={isExpanded ? 'bg-[#222222] rounded-t-[10px]' : ''}
      />
      {isExpanded && (
        <div className="sticky left-0 z-[1]" style={EXPANDED_PANEL_STYLE}>
          <EntityView
            type="Lead"
            entityId={lead.id}
            variant="page"
            pageHasEntityRowAbove
            leadLockedUnitId={leadLockedUnitId}
            className="h-auto overflow-hidden rounded-b-[10px] border-b border-[#2A2A2A] !ml-0"
          />
        </div>
      )}
    </div>
  ), [leadLockedUnitId, rowActions]);

  return (
    <Table
      glass={glass}
      beforeCreateContent={toolbarLead}
      entityType="Lead"
      initialFilters={initialFilters}
      lockedFilters={lockedFilters}
      clientRowFilter={clientRowFilter}
      emptyStateContent={emptyStateContent}
      afterRowsContent={afterRowsContent}
      liveResort={liveResort}
      rescoreActive={rescoreActive}
      scopedIds={scopedIds}
      disableManualRefresh={disableManualRefresh}
      onManualRefresh={onManualRefresh}
      filterOptions={filterOptions}
      columns={columns}
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      sortOptions={sortOptions}
      renderToolbarExtras={effectiveToolbarHeader}
      toolbarContextMenuActions={toolbarContextMenuActions}
      onVisibleItemIdsChange={handleVisibleIdsChange}
      bulkActions={effectiveBulkActions}
      rowActions={rowActions}
      liveRefresh={liveRefresh}
      enableSelection={enableSelection}
      controlledSelectionIds={controlledSelectionIds}
      onControlledSelectionChange={onControlledSelectionChange}
      inlineExpand={inlineExpand}
      onExpandedIdChange={onExpandedIdChange}
      expandedId={expandedId}
      renderRow={renderLeadRow}
    />
  );
});

LeadsTable.displayName = 'LeadsTable';
